/* eslint-disable max-lines */
/* eslint-disable max-lines-per-function */
import React from "react";
import { Link } from "react-router-dom";
import { ApolloQueryResult, OperationVariables, useMutation } from "@apollo/client";
import { gql } from "graphql-tag";
import MaterialTable, { Column } from "@material-table/core";
import { Grid } from "@mui/material";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import SmartphoneIcon from "@mui/icons-material/Smartphone";
import AlternateEmailIcon from "@mui/icons-material/AlternateEmail";
import MailIcon from "@mui/icons-material/Mail";
import PublicIcon from "@mui/icons-material/Public";
import HomeIcon from "@mui/icons-material/Home";
import PersonIcon from "@mui/icons-material/Person";
import EditIcon from "@mui/icons-material/Edit";
import { styled } from "@mui/material/styles";
import { useSnackbar } from "components/haven-design/snackbar";
import tooltip from "components/tooltip/tooltip.module.css";
import { DuplicateTemplateGroup,
  DuplicateTemplateGroupVariables,
  templateGroups_templateGroups as Template,
  templateGroups } from "types/gql-types";
import logger from "services/logger";
import { Viewer } from "components/auth/types";
import { grabEmailTemplate, grabLetterTemplate, grabSmsTemplate } from "utils/template";
import { RemoveTemplateModal } from "../template-groups/remove-template-modal";

const selectPageSize = (desiredPageSize: number, pageSizeOptions: number[]): number => {
  let left = 0;
  let right = pageSizeOptions.length - 1;
  let result: number | null = null;

  while (left <= right) {
    const mid = Math.floor((left + right) / 2);

    if (pageSizeOptions[mid] > desiredPageSize) {
      result = pageSizeOptions[mid];
      right = mid - 1;
    } else {
      left = mid + 1;
    }
  }

  return result || pageSizeOptions[0];
};

type RefetchTemplates = (variables?: Partial<OperationVariables> | undefined) =>
Promise<ApolloQueryResult<templateGroups>>
export const DUPLICATE_TEMPLATE = gql`
  mutation DuplicateTemplateGroup($id: ID!) {
    duplicateTemplateGroup(id: $id) {
      id
    }
  }
`;

// Create styled menu item with standard dark grey color and hover effect
const StyledMenuItem = styled(MenuItem)({
  color: "#32445a", // Standard navy/dark grey color
  fontWeight: 400,
  fontSize: "14px",
  padding: "8px 16px",
  "&:hover": {
    backgroundColor: "rgba(0, 0, 0, 0.04)", // Light grey on hover
  },
});

// Create a Link MenuItem component to handle navigation
const LinkMenuItem = (props: {
  to: string;
  onClick: () => void;
  children: React.ReactNode;
}) => (
  <MenuItem
    onClick={props.onClick}
    style={{
      color: "#32445a",
      fontWeight: 400,
      fontSize: "14px",
      padding: "8px 16px",
    }}
    component={Link as React.ElementType}
    to={props.to}
  >
    {props.children}
  </MenuItem>
);

// Create styled IconButton with consistent hover effect
const StyledIconButton = styled(IconButton)({
  padding: "4px",
  "&:hover": {
    backgroundColor: "rgba(0, 0, 0, 0.04)", // Light grey on hover
  },
});

// Create a custom delete component that uses the same mutation as RemoveTemplate
const DeleteAction: React.FC<{
  onShowModal: () => void;
}> = ({ onShowModal }) => (
  <span onClick={onShowModal} style={{ color: "#32445a", cursor: "pointer" }}>
    Delete
  </span>
);

// New component for actions menu
const ActionsMenu: React.FC<{
  duplicateAction: () => void,
  template: Template,
  refetchTemplates: RefetchTemplates,
}> = ({ duplicateAction, template, refetchTemplates }) => {
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [showModal, setShowModal] = React.useState(false);
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleDuplicate = (e: React.MouseEvent<HTMLLIElement>) => {
    e.preventDefault();
    duplicateAction();
    handleClose();
  };

  const handleShowModal = () => {
    setShowModal(true);
    handleClose();
  };

  // Always show the dropdown menu with three dots for all screen sizes
  return (
    <div>
      <StyledIconButton
        aria-label="more"
        aria-controls="long-menu"
        aria-haspopup="true"
        onClick={handleClick}
        size="small"
      >
        <MoreVertIcon style={{ color: "#32445a" }} />
      </StyledIconButton>
      <Menu
        id="actions-menu"
        anchorEl={anchorEl}
        keepMounted
        open={open}
        onClose={handleClose}
      >
        <LinkMenuItem
          onClick={handleClose}
          to={`/dash/manager/settings/templates/update/${template.id}`}
        >
          Edit
        </LinkMenuItem>
        <StyledMenuItem onClick={handleDuplicate}>
          Copy
        </StyledMenuItem>
        <StyledMenuItem>
          <DeleteAction
            onShowModal={handleShowModal}
          />
        </StyledMenuItem>
      </Menu>
      <RemoveTemplateModal
        templateId={template.id}
        onSuccess={() => {
          refetchTemplates();
        }}
        controls={{
          showModal,
          setShowModal,
        }}
      />
    </div>
  );
};

const EditableActionColumn: React.FC<{
  duplicateAction: () => void
  rowData: Template,
  refetchTemplates: RefetchTemplates,
}> = ({
  rowData,
  duplicateAction,
  refetchTemplates,
}) => (
  <ActionsMenu
    template={rowData}
    duplicateAction={duplicateAction}
    refetchTemplates={refetchTemplates}
  />
);

const ActionColumn = (props: {
  rowData: Template,
  viewer: Viewer,
  refetchTemplates: RefetchTemplates,
}) => {
  const { rowData, refetchTemplates } = props;
  const [duplicate] = useMutation<DuplicateTemplateGroup, DuplicateTemplateGroupVariables>(
    DUPLICATE_TEMPLATE,
  );
  const { openSnackbar } = useSnackbar();
  const duplicateAction = () =>
    duplicate({ variables: { id: rowData.id } })
      .then(() => refetchTemplates())
      .catch((err) => {
        logger.sendError(err);
        openSnackbar({ message: "Error copying template", status: "error" });
      });

  // Delete/Edit visibility must mirror the backend `canModify` authorization so
  // we never surface a Delete action that the server will reject.
  if (rowData.canModify) {
    return <EditableActionColumn
      {...props}
      duplicateAction={duplicateAction}
      refetchTemplates={refetchTemplates}
    />;
  }

  // For read-only templates, just show a menu with Copy action
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleDuplicate = (e: React.MouseEvent<HTMLLIElement>) => {
    e.preventDefault();
    duplicateAction();
    handleClose();
  };


  return (
    <div>
      <StyledIconButton
        aria-label="more"
        aria-controls="copy-menu"
        aria-haspopup="true"
        onClick={handleClick}
        size="small"
      >
        <MoreVertIcon style={{ color: "#32445a" }} />
      </StyledIconButton>
      <Menu
        id="copy-menu"
        anchorEl={anchorEl}
        keepMounted
        open={open}
        onClose={handleClose}
      >
        <StyledMenuItem onClick={handleDuplicate}>
          Copy
        </StyledMenuItem>
      </Menu>
    </div>
  );
};

const SmsColumn = (rowData: Template) => {
  const smsTemplate = grabSmsTemplate(rowData.templates);
  if (smsTemplate && !smsTemplate.isEmpty) {
    return <SmartphoneIcon style={{ fontSize: "1rem" }} />;
  }
  return null;
};

const EmailColumn = (rowData: Template) => {
  const emailTemplate = grabEmailTemplate(rowData.templates);
  if (emailTemplate && !emailTemplate.isEmpty) {
    return <AlternateEmailIcon style={{ fontSize: "1rem" }} />;
  }
  return null;
};

const getUpdatedAt = (rowData: Template) => {
  const dates = rowData.templates
    .map(({ updatedAt }) => (updatedAt === null ? 0 : new Date(updatedAt).getTime()));
  return Math.max(...dates);
};

const UpdatedAtColumn = (rowData: Template) => {
  const maxDate = getUpdatedAt(rowData);
  if (maxDate) return new Date(maxDate).toDateString();
  return null;
};

const sortUpdatedAt = (a: Template, b: Template) => getUpdatedAt(a) - getUpdatedAt(b);

const MailColumn = (rowData: Template) => {
  const mailTemplate = grabLetterTemplate(rowData.templates);
  if (mailTemplate && !mailTemplate.isEmpty) {
    return <MailIcon style={{ fontSize: "1rem" }} />;
  }
  return null;
};

const getOwnershipTypeSortKey = ({ name, isOrgTemplate, isPropertyTemplate }: Template) => {
  // Create the sort key combining the access type
  // and the template name in a string
  let ownershipIndex = 2;
  if (isOrgTemplate) {
    ownershipIndex = 0;
  } else if (isPropertyTemplate) {
    ownershipIndex = 1;
  }

  return `${ownershipIndex} || ${name}`;
};

const sortOwnershipType = (a: Template, b: Template) =>
  getOwnershipTypeSortKey(a).localeCompare(getOwnershipTypeSortKey(b));

const OwnershipColumn = (rowData: Template) => {
  if (rowData.isOrgTemplate) {
    return (
      <div style={{ whiteSpace: "nowrap" }}>
        <PublicIcon style={{ fontSize: "1rem", marginRight: 8, verticalAlign: "middle" }} />
        <span>Organization</span>
      </div>
    );
  }

  if (rowData.isPropertyTemplate) {
    return (
      <div style={{ whiteSpace: "nowrap" }}>
        <HomeIcon style={{ fontSize: "1rem", marginRight: 8, verticalAlign: "middle" }} />
        <span
          className={rowData.properties.length > 1 ? tooltip.tooltip : ""}
          data-tooltip={rowData.properties.map((prop: { name: string; }) => prop.name).join(", ")}>
          {rowData.properties[0].name}
          {rowData.properties.length > 1 && "..."}
        </span>
      </div>
    );
  }

  return (
    <div style={{ whiteSpace: "nowrap" }}>
      <PersonIcon style={{ fontSize: "1rem", marginRight: 8, verticalAlign: "middle" }} />
      <span>{rowData.user?.profile?.fullName || rowData.userId }</span>
    </div>
  );
};
const LOCAL_STORAGE_KEY = "template-table-state";
type TableFilterState = {
  orderBy?: number,
  orderDirection?: "asc" | "desc",
  searchText?: string
}
const storeTableState = (input: TableFilterState) => {
  const storedJson = localStorage.getItem(LOCAL_STORAGE_KEY) || "{}";
  const data = JSON.parse(storedJson);

  localStorage.setItem(
    LOCAL_STORAGE_KEY,
    JSON.stringify({ ...data, ...input }),
  );
};

const loadTableState = (): TableFilterState | null => {
  const storedJson = localStorage.getItem(LOCAL_STORAGE_KEY);

  if (!storedJson) return null;

  return JSON.parse(storedJson) || null;
};


const TemplateTable: React.FC<{
  viewer: Viewer,
  rowData: Template[],
  refetchTemplates: RefetchTemplates
}> = ({ rowData, viewer, refetchTemplates }) => {
  const pageSizeOptions = [5, 10, 15, 25, 50, 100];
  const pageSize = selectPageSize(Math.min(rowData.length, 25), pageSizeOptions);
  const tableState = loadTableState();
  const extraOptions = tableState && tableState.searchText ? (
    { searchText: tableState.searchText }) : {};
  const defaultSort = tableState && typeof tableState.orderBy === "number" && tableState.orderBy > -1 ? (
    { orderBy: tableState.orderBy, orderDirection: tableState.orderDirection }) : (
    { orderBy: 0, orderDirection: "asc" });
  const columns: Array<Column<Template>> = [
    { title: "Name",
      field: "name",
      width: "30%",
      render: ({ name, id }) => (<div style={{ maxWidth: "500px" }}>
        <Link to={`/dash/manager/settings/templates/update/${id}`}>
          {name}
          <EditIcon style={{ fontSize: "1rem", marginLeft: 4, verticalAlign: "middle" }} />
        </Link>
      </div>) },
    { title: "Access",
      field: "access",
      width: "20%",
      render: OwnershipColumn,
      customSort: sortOwnershipType },
    { title: "SMS",
      field: "sms",
      width: "8%",
      sorting: false,
      render: SmsColumn },
    { title: "Email",
      field: "email",
      width: "8%",
      sorting: false,
      render: EmailColumn },
    { title: "Letter",
      field: "letter",
      width: "8%",
      sorting: false,
      render: MailColumn },
    { title: "Last Updated",
      field: "updatedAt",
      width: "16%",
      render: UpdatedAtColumn,
      customSort: sortUpdatedAt },
    { title: "Actions",
      field: "body",
      width: "8%",
      sorting: false,
      render: (data: Template) => (
        <ActionColumn rowData={data} viewer={viewer} refetchTemplates={refetchTemplates}/>) },

  ];
  columns[defaultSort.orderBy].defaultSort = (defaultSort.orderDirection || "asc") as "asc" | "desc";

  return (<MaterialTable
    onSearchChange={(searchText) => {
      storeTableState({ searchText });
    }}
    onOrderChange={(orderBy: number, orderDirection: "asc" | "desc") => {
      storeTableState({ orderBy, orderDirection });
    }}
    columns={columns}
    components={{ Container: Grid }}
    data={rowData}
    options={{
      pageSize,
      pageSizeOptions,
      draggable: false,
      showTitle: false,
      padding: "dense",
      search: true,
      toolbar: true,
      tableLayout: "fixed",
      headerStyle: {
        // Ensure header text doesn't wrap
        whiteSpace: "nowrap",
      },
      ...extraOptions,
    }}
  />);
};

export default TemplateTable;
